import React, { useState, useEffect, useMemo } from 'react';
import axios from '../utils/axiosConfig';
import { formatCurrency } from '../utils/currency';
import { t } from '../utils/translations';
import { priceBreakdown, GST_RATES, gstRateSelectValue } from '../utils/gst';
import { useAuth } from '../context/AuthContext';
import ProductBarcodePrint from '../components/ProductBarcodePrint';
import ProductImage, { clearProductImageCache } from '../components/ProductImage';
import SearchableSelect from '../components/SearchableSelect';
import SearchableProductSelect from '../components/SearchableProductSelect';
import KebabMenu, { MENU_ICONS as ICONS, useKebabMenu } from '../components/KebabMenu';
import DateInput from '../components/DateInput';
import { expiryStatus, expiryLabel } from '../utils/expiry';
import { formatDate } from '../utils/dates';
import {
  autoConversionToBase,
  isLikelyFractionalUnit,
  convertQtyBetweenUnits,
  normalizeUnitCode,
  pickDefaultUom,
  openPriceLayers,
  layerUnitPrice,
  layerUnitCost,
  effectiveSalePrice,
  effectivePurchasePrice,
  fromBaseQty,
  formatQty,
  formatStockWithRetail,
  formatLayerQty,
  piecesFromInnerFactor,
  innerFactorFromPieces,
  snapInnerPackConversion
} from '../utils/uom';
import './Products.css';

const PAGE_SIZE = 20;

const Products = () => {
  const { user, isAdmin } = useAuth();
  // Product GST settings available to admin even if client GST is disabled (for GST bill override)
  const gstEnabled = isAdmin || Boolean(user?.gst_enabled);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Store all products for filtering
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [printProducts, setPrintProducts] = useState(null);
  const [generatingBarcodes, setGeneratingBarcodes] = useState(false);
  const [printingProductId, setPrintingProductId] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({
    name: '',
    category_id: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    barcode: '',
    description: '',
    category_id: '',
    subcategory_id: '',
    price: '',
    cost: '',
    stock_quantity: '',
    unit: 'pcs',
    status: 'active',
    gst_rate: '18',
    gst_inclusive: false,
    expiry_date: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [removeImage, setRemoveImage] = useState(false);
  const [uoms, setUoms] = useState([
    {
      code: 'pcs',
      name: 'pcs',
      is_base: true,
      conversion_to_base: 1,
      is_sales_default: true,
      is_purchase_default: true,
      sale_price: '',
      purchase_price: '',
      barcode: '',
      allow_fractional: false
    }
  ]);
  /** Enter stock in a friendly unit (e.g. kg) while storing base (e.g. g). */
  const [stockEntryUnit, setStockEntryUnit] = useState('pcs');
  const [stockEntryQty, setStockEntryQty] = useState('');
  const [showUomExtra, setShowUomExtra] = useState(false);
  const [innerCount, setInnerCount] = useState('20');
  const [innerName, setInnerName] = useState('Packet');
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpProduct, setTopUpProduct] = useState(null);
  const [topUpQty, setTopUpQty] = useState('');
  const [topUpUomId, setTopUpUomId] = useState('');
  const [topUpPrice, setTopUpPrice] = useState('');
  const [topUpCost, setTopUpCost] = useState('');
  const [topUpSaving, setTopUpSaving] = useState(false);
  const [editingTopUp, setEditingTopUp] = useState(null);
  const [showTopUpHistory, setShowTopUpHistory] = useState(false);
  const [topUpHistory, setTopUpHistory] = useState([]);
  const [topUpHistoryProductId, setTopUpHistoryProductId] = useState(null);
  const [topUpHistoryLoading, setTopUpHistoryLoading] = useState(false);
  const [topUpDeletingId, setTopUpDeletingId] = useState(null);
  const [costReport, setCostReport] = useState(null);
  const [costReportBusy, setCostReportBusy] = useState(false);

  const blankUom = (isBase = false) => ({
    code: '',
    name: '',
    is_base: isBase,
    conversion_to_base: isBase ? 1 : '',
    is_sales_default: isBase,
    is_purchase_default: isBase,
    sale_price: '',
    purchase_price: '',
    barcode: '',
    allow_fractional: false
  });

  const defaultBaseUom = () => ({
    ...blankUom(true),
    code: 'pcs',
    name: 'Pieces'
  });

  const baseUomCode = uoms.find((u) => u.is_base)?.code || formData.unit || 'pcs';

  const applyStockEntryToBase = (qty, entryUnit, baseCode) => {
    const q = parseFloat(qty);
    if (!Number.isFinite(q)) {
      setFormData((fd) => ({ ...fd, stock_quantity: '' }));
      return;
    }
    const entry = entryUnit || baseCode;
    let baseQty = convertQtyBetweenUnits(q, entry, baseCode);
    if (baseQty == null) {
      // Fall back to product UOM conversion factor
      const uom = uoms.find((u) => normalizeUnitCode(u.code) === normalizeUnitCode(entry));
      if (uom && !uom.is_base && parseFloat(uom.conversion_to_base) > 0) {
        baseQty = Math.round(q * parseFloat(uom.conversion_to_base) * 1e6) / 1e6;
      } else if (normalizeUnitCode(entry) === normalizeUnitCode(baseCode)) {
        baseQty = q;
      } else {
        baseQty = q;
      }
    }
    setFormData((fd) => ({ ...fd, stock_quantity: String(baseQty) }));
  };

  const syncUomCode = (index, code) => {
    setUoms((prev) => {
      const base = prev.find((r) => r.is_base);
      const baseCode = base?.code || code;
      return prev.map((row, i) => {
        if (i !== index) {
          // If base code changed, refresh auto conversions on alternates
          if (prev[index]?.is_base && !row.is_base) {
            const auto = autoConversionToBase(row.code, code);
            if (auto != null) {
              return {
                ...row,
                conversion_to_base: auto,
                allow_fractional: row.allow_fractional || isLikelyFractionalUnit(row.code)
              };
            }
          }
          return row;
        }
        const next = {
          ...row,
          code,
          name: row.name && row.name !== row.code ? row.name : code,
          allow_fractional: row.allow_fractional || isLikelyFractionalUnit(code)
        };
        if (next.is_base) {
          next.conversion_to_base = 1;
        } else {
          const auto = autoConversionToBase(code, baseCode);
          if (auto != null) next.conversion_to_base = auto;
        }
        return next;
      });
    });
    if (uoms[index]?.is_base) {
      setFormData((fd) => ({ ...fd, unit: code || fd.unit }));
      setStockEntryUnit((prev) => {
        // Prefer kg entry when base becomes g
        const n = normalizeUnitCode(code);
        if (n === 'g' || n === 'gram' || n === 'grams') return 'kg';
        return code || prev;
      });
    }
  };

  const COMMON_UNITS = [
    { code: 'pcs', name: 'Pieces' },
    { code: 'Bottle', name: 'Bottle' },
    { code: 'Box', name: 'Box' },
    { code: 'Carton', name: 'Carton' },
    { code: 'Bag', name: 'Bag' },
    { code: 'Bdl', name: 'Bundle' },
    { code: 'Packet', name: 'Packet' },
    { code: 'Pack', name: 'Pack' },
    { code: 'kg', name: 'Kilogram' },
    { code: 'g', name: 'Gram' },
    { code: 'L', name: 'Liter' },
    { code: 'ml', name: 'Milliliter' },
    { code: 'm', name: 'Meter' }
  ];

  const addSmallerPiece = () => {
    const per = parseFloat(innerCount);
    if (!(per > 1)) {
      alert('Enter how many pieces are inside one pack (for example 20).');
      return;
    }
    const name = String(innerName || 'Packet').trim() || 'Packet';
    const factor = innerFactorFromPieces(per);
    setUoms((prev) => {
      const idx = prev.findIndex((u) => !u.is_base && normalizeUnitCode(u.code) === normalizeUnitCode(name));
      const row = {
        ...(idx >= 0 ? prev[idx] : blankUom(false)),
        code: name,
        name,
        is_base: false,
        conversion_to_base: factor,
        is_sales_default: true,
        is_purchase_default: false,
        allow_fractional: false
      };
      const next = prev.map((u) => ({ ...u, is_sales_default: false }));
      if (idx >= 0) next[idx] = { ...next[idx], ...row, id: prev[idx].id };
      else next.push(row);
      return next;
    });
  };

  const applyPackPreset = () => {
    const perBox = 20;
    setUoms([
      {
        code: 'Bottle',
        name: 'Bottle',
        is_base: true,
        conversion_to_base: 1,
        is_sales_default: true,
        is_purchase_default: false,
        sale_price: '',
        purchase_price: '',
        barcode: '',
        allow_fractional: false
      },
      {
        code: 'Box',
        name: 'Box',
        is_base: false,
        conversion_to_base: perBox,
        is_sales_default: false,
        is_purchase_default: true,
        sale_price: '',
        purchase_price: '',
        barcode: '',
        allow_fractional: false
      }
    ]);
    setStockEntryUnit('Box');
    const qty = parseFloat(stockEntryQty);
    setFormData((fd) => ({
      ...fd,
      unit: 'Bottle',
      stock_quantity: Number.isFinite(qty) ? String(Math.round(qty * perBox * 1e6) / 1e6) : fd.stock_quantity
    }));
  };

  const setBaseUnitCode = (code) => {
    const name = COMMON_UNITS.find((u) => u.code === code)?.name || code;
    setUoms((prev) => {
      const hasBase = prev.some((u) => u.is_base);
      if (!hasBase) {
        return [{
          ...blankUom(true),
          code,
          name,
          allow_fractional: isLikelyFractionalUnit(code)
        }];
      }
      return prev.map((row) => {
        if (!row.is_base) {
          const auto = autoConversionToBase(row.code, code);
          if (auto != null) {
            return { ...row, conversion_to_base: auto };
          }
          return row;
        }
        return {
          ...row,
          code,
          name: row.name === row.code || !row.name ? name : row.name,
          conversion_to_base: 1,
          allow_fractional: row.allow_fractional || isLikelyFractionalUnit(code)
        };
      });
    });
    setFormData((fd) => ({ ...fd, unit: code }));
    setStockEntryUnit(code);
  };

  const applyWeightPreset = () => {
    setUoms([
      {
        code: 'kg',
        name: 'Kilogram',
        is_base: true,
        conversion_to_base: 1,
        is_sales_default: true,
        is_purchase_default: true,
        sale_price: '',
        purchase_price: '',
        barcode: '',
        allow_fractional: true
      },
      {
        code: 'g',
        name: 'Gram',
        is_base: false,
        conversion_to_base: 0.001,
        is_sales_default: false,
        is_purchase_default: false,
        sale_price: '',
        purchase_price: '',
        barcode: '',
        allow_fractional: true
      },
      {
        code: '500g',
        name: '500 g pack',
        is_base: false,
        conversion_to_base: 0.5,
        is_sales_default: false,
        is_purchase_default: false,
        sale_price: '',
        purchase_price: '',
        barcode: '',
        allow_fractional: false
      }
    ]);
    setFormData((fd) => ({ ...fd, unit: 'kg' }));
    setStockEntryUnit('kg');
    if (stockEntryQty !== '') {
      applyStockEntryToBase(stockEntryQty, 'kg', 'kg');
    }
  };

  const uomsFromProduct = (product) => {
    const list = Array.isArray(product?.uoms) ? product.uoms : [];
    if (list.length) {
      return list.map((u) => ({
        id: u.id,
        code: u.code || '',
        name: u.name || u.code || '',
        is_base: Boolean(Number(u.is_base)),
        conversion_to_base: u.is_base ? 1 : (u.conversion_to_base ?? ''),
        is_sales_default: Boolean(Number(u.is_sales_default)),
        is_purchase_default: Boolean(Number(u.is_purchase_default)),
        sale_price: u.sale_price != null ? String(u.sale_price) : '',
        purchase_price: u.purchase_price != null ? String(u.purchase_price) : '',
        barcode: u.barcode || '',
        allow_fractional: Boolean(Number(u.allow_fractional))
      }));
    }
    const unit = product?.unit || 'pcs';
    return [{
      ...blankUom(true),
      code: unit,
      name: unit,
      barcode: product?.barcode || '',
      sale_price: product?.price != null ? String(product.price) : '',
      purchase_price: product?.cost != null ? String(product.cost) : ''
    }];
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get('/api/products'),
        axios.get('/api/categories')
      ]);
      const productsData = productsRes.data.products || [];
      setProducts(productsData);
      setAllProducts(productsData); // Store all products for filtering
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMessage = error.response?.data?.message || error.message || t('Failed to fetch data');
      alert(`${t('Failed to fetch data')}: ${errorMessage}`);
      setProducts([]);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcategories when category changes
  useEffect(() => {
    if (formData.category_id) {
      axios.get(`/api/subcategories?category_id=${formData.category_id}`)
        .then(response => {
          setSubcategories(response.data.subcategories || []);
        })
        .catch(error => {
          console.error('Error fetching subcategories:', error);
          setSubcategories([]);
        });
    } else {
      setSubcategories([]);
      setFormData(prev => ({ ...prev, subcategory_id: '' }));
    }
  }, [formData.category_id]);

  // Filter products based on search criteria
  useEffect(() => {
    let filtered = [...allProducts];

    // Filter by product name / SKU / barcode
    if (searchFilters.name) {
      const q = searchFilters.name.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(q) ||
        product.sku?.toLowerCase().includes(q) ||
        String(product.barcode || '').toLowerCase().includes(q)
      );
    }

    // Filter by category
    if (searchFilters.category_id) {
      filtered = filtered.filter(product =>
        product.category_id === searchFilters.category_id
      );
    }

    setProducts(filtered);
    setCurrentPage(1);
  }, [searchFilters, allProducts]);

  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = products.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(safePage * PAGE_SIZE, products.length);

  const paginatedProducts = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return products.slice(start, start + PAGE_SIZE);
  }, [products, safePage]);

  const goToPage = (page) => {
    const next = Math.max(1, Math.min(totalPages, page));
    setCurrentPage(next);
  };

  // Update search filters
  const handleSearchChange = (field, value) => {
    setSearchFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear all search filters
  const clearSearch = () => {
    setSearchFilters({
      name: '',
      category_id: ''
    });
  };

  const saveProductPayload = async (payload, { forceOldPrice = false } = {}) => {
    const data = forceOldPrice
      ? { ...payload, price: Number(editingProduct.price) || 0 }
      : payload;

    if (imageFile || removeImage) {
      const fd = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'boolean') {
          fd.append(key, value ? '1' : '0');
        } else if (typeof value === 'object') {
          fd.append(key, JSON.stringify(value));
        } else {
          fd.append(key, String(value));
        }
      });
      if (imageFile) fd.append('image', imageFile);
      if (removeImage && !imageFile) fd.append('remove_image', '1');

      if (editingProduct) {
        await axios.put(`/api/products/${editingProduct.id}`, fd);
        clearProductImageCache(editingProduct.id);
      } else {
        await axios.post('/api/products', fd);
      }
    } else if (editingProduct) {
      await axios.put(`/api/products/${editingProduct.id}`, data);
    } else {
      await axios.post('/api/products', data);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fallbackUnit = String(formData.unit || 'pcs').trim() || 'pcs';
      const uomsToSave = uoms.filter((u) => {
        if (u.is_base) return true;
        const code = String(u.code || u.name || '').trim();
        const factor = parseFloat(u.conversion_to_base);
        return Boolean(code) || factor > 0;
      });
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost || 0),
        stock_quantity: parseFloat(formData.stock_quantity || 0),
        gst_rate: gstEnabled ? parseFloat(formData.gst_rate || 0) : 0,
        gst_inclusive: gstEnabled ? Boolean(formData.gst_inclusive) : false,
        unit: (uoms.find((u) => u.is_base)?.code || fallbackUnit).trim() || fallbackUnit,
        uoms: uomsToSave.map((u, idx) => {
          const code = String(u.code || u.name || (u.is_base ? fallbackUnit : '')).trim();
          return {
            id: u.id,
            code,
            name: String(u.name || u.code || code).trim(),
            is_base: Boolean(u.is_base),
            conversion_to_base: u.is_base
              ? 1
              : snapInnerPackConversion(parseFloat(u.conversion_to_base)),
            is_sales_default: Boolean(u.is_sales_default),
            is_purchase_default: Boolean(u.is_purchase_default),
            sale_price: u.is_base
              ? parseFloat(formData.price)
              : (u.sale_price !== '' && u.sale_price != null ? parseFloat(u.sale_price) : null),
            purchase_price: u.purchase_price !== '' && u.purchase_price != null ? parseFloat(u.purchase_price) : null,
            barcode: u.barcode && String(u.barcode).trim() ? String(u.barcode).trim() : null,
            allow_fractional: Boolean(u.allow_fractional),
            sort_order: idx
          };
        })
      };
      const nextBarcode = payload.barcode && String(payload.barcode).trim()
        ? String(payload.barcode).trim()
        : null;
      const previousBarcode = editingProduct?.barcode && String(editingProduct.barcode).trim()
        ? String(editingProduct.barcode).trim()
        : null;
      payload.uoms = payload.uoms.map((u) => {
        if (!u.is_base) return u;
        if (!u.barcode || u.barcode === previousBarcode) {
          return { ...u, barcode: nextBarcode };
        }
        return u;
      });

      if (!payload.uoms.some((u) => u.is_base)) {
        alert('Exactly one base UOM is required');
        return;
      }
      if (payload.uoms.some((u) => !u.code)) {
        alert('Name each extra pack (for example Box) before saving.');
        return;
      }
      if (payload.uoms.some((u) => !u.is_base && !(u.conversion_to_base > 0))) {
        alert('Alternate UOMs need a conversion factor greater than 0 (how many base units)');
        return;
      }

      if (editingProduct) {
        await saveProductPayload(payload);
      } else {
        await saveProductPayload(payload);
      }

      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to save product'));
    }
  };

  const handleEdit = async (product) => {
    setEditingProduct(product);
    setImageFile(null);
    setImagePreview('');
    setRemoveImage(false);
    let productDetail = product;
    try {
      const res = await axios.get(`/api/products/${product.id}`);
      if (res.data?.product) productDetail = res.data.product;
    } catch (_) { /* use list row */ }
    if (!productDetail.barcode || !String(productDetail.barcode).trim()) {
      try {
        const generated = await axios.post(`/api/products/${product.id}/generate-barcode`);
        if (generated.data?.product?.barcode) {
          const barcode = generated.data.product.barcode;
          const uoms = Array.isArray(productDetail.uoms)
            ? productDetail.uoms.map((u) => (
              Number(u.is_base) && !String(u.barcode || '').trim() ? { ...u, barcode } : u
            ))
            : productDetail.uoms;
          productDetail = { ...productDetail, ...generated.data.product, barcode, uoms };
        }
      } catch (_) { /* keep the form usable if generation fails */ }
    }
    setUoms(uomsFromProduct(productDetail));
    const baseCode = (productDetail.uoms || []).find((u) => Number(u.is_base))?.code
      || productDetail.unit
      || 'pcs';
    const n = normalizeUnitCode(baseCode);
    const entryUnit = (n === 'g' || n === 'gram' || n === 'grams') ? 'kg' : baseCode;
    setStockEntryUnit(entryUnit);
    const stockBase = productDetail.stock_quantity;
    if (stockBase != null && stockBase !== '') {
      const shown = convertQtyBetweenUnits(stockBase, baseCode, entryUnit);
      setStockEntryQty(shown != null ? String(shown) : String(stockBase));
    } else {
      setStockEntryQty('');
    }
    setFormData({
      name: productDetail.name || '',
      sku: productDetail.sku || '',
      barcode: productDetail.barcode || '',
      description: productDetail.description || '',
      category_id: productDetail.category_id || '',
      subcategory_id: productDetail.subcategory_id || '',
      price: productDetail.price || '',
      cost: productDetail.cost || '',
      stock_quantity: productDetail.stock_quantity || '',
      unit: productDetail.unit || 'pcs',
      status: productDetail.status || 'active',
      gst_rate: gstRateSelectValue(productDetail.gst_rate, '18'),
      gst_inclusive: Boolean(Number(productDetail.gst_inclusive)),
      expiry_date: productDetail.expiry_date ? String(productDetail.expiry_date).slice(0, 10) : ''
    });
    
    // Fetch subcategories for the selected category
    if (productDetail.category_id) {
      try {
        const response = await axios.get(`/api/subcategories?category_id=${productDetail.category_id}`);
        setSubcategories(response.data.subcategories || []);
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this product?'))) {
      return;
    }

    try {
      await axios.delete(`/api/products/${id}`);
      fetchData();
    } catch (error) {
      alert(t('Failed to delete product'));
    }
  };

  const topUpUom = (product, uomId) => {
    const list = product?.uoms || [];
    return list.find((u) => String(u.id) === String(uomId)) || pickDefaultUom(list, 'sales');
  };

  const suggestedTopUpPrice = (product, uom) => {
    const layers = openPriceLayers(product);
    if (layers[0]) {
      return layerUnitPrice(uom, layers[0].sale_price, product.price);
    }
    return effectiveSalePrice(uom, product.price);
  };

  const suggestedTopUpCost = (product, uom) => {
    const layers = openPriceLayers(product);
    if (layers[0] && layers[0].cost_price != null) {
      return layerUnitCost(uom, layers[0].cost_price, product.cost);
    }
    return effectivePurchasePrice(uom, product.cost);
  };

  const fillTopUpFromProduct = (detail) => {
    const uom = pickDefaultUom(detail?.uoms || [], 'sales');
    setTopUpProduct(detail);
    setTopUpUomId(uom?.id || '');
    setTopUpQty('');
    const price = suggestedTopUpPrice(detail, uom);
    const cost = suggestedTopUpCost(detail, uom);
    setTopUpPrice(Number.isFinite(price) ? String(price) : '');
    setTopUpCost(Number.isFinite(cost) ? String(cost) : '');
    loadTopUpHistory(detail?.id);
  };

  const loadTopUpHistory = async (productId) => {
    setTopUpHistoryLoading(true);
    try {
      const url = productId ? `/api/products/${productId}/top-ups` : '/api/products/top-ups';
      const res = await axios.get(url);
      setTopUpHistory(res.data.topups || []);
    } catch (error) {
      setTopUpHistory([]);
    } finally {
      setTopUpHistoryLoading(false);
    }
  };

  const openEditTopUp = async (row) => {
    if (!row?.id || !row.product_id) return;
    setEditingTopUp(row);
    setShowTopUp(true);
    setTopUpQty(row.qty_uom != null ? String(row.qty_uom) : '');
    setTopUpPrice(row.sale_price != null ? String(row.sale_price) : '');
    setTopUpCost(row.cost_price != null ? String(row.cost_price) : '');
    setTopUpUomId(row.uom_id || '');
    try {
      const res = await axios.get(`/api/products/${row.product_id}`);
      setTopUpProduct(res.data.product || { id: row.product_id, name: row.product_name });
    } catch (error) {
      setTopUpProduct({ id: row.product_id, name: row.product_name, unit: row.product_unit });
    }
    loadTopUpHistory(showTopUpHistory ? topUpHistoryProductId : row.product_id);
  };

  const closeTopUp = () => {
    setShowTopUp(false);
    setEditingTopUp(null);
    setTopUpProduct(null);
  };

  const openTopUpHistory = (product) => {
    const id = product?.id || null;
    setTopUpHistoryProductId(id);
    setShowTopUpHistory(true);
    loadTopUpHistory(id);
  };

  const handleDeleteTopUp = async (row) => {
    if (!row?.id || !row.product_id) return;
    if (!window.confirm(
      row.legacy
        ? t('Delete this old top-up and reverse only that leftover stock? Other stock for this product stays.')
        : t('Delete this top-up and reverse the stock that is still unsold?')
    )) {
      return;
    }
    setTopUpDeletingId(row.id);
    try {
      await axios.delete(`/api/products/${row.product_id}/top-ups/${row.id}`);
      await fetchData();
      if (showTopUpHistory) {
        await loadTopUpHistory(topUpHistoryProductId);
      } else {
        await loadTopUpHistory(row.product_id);
      }
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to delete top-up'));
    } finally {
      setTopUpDeletingId(null);
    }
  };

  const formatTopUpWhen = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  };

  const renderTopUpHistoryTable = (rows, showProduct) => {
    if (topUpHistoryLoading) {
      return <p>{t('Loading...')}</p>;
    }
    if (!rows.length) {
      return <p style={{ color: '#666' }}>{t('No top-ups yet.')}</p>;
    }
    return (
      <div className="topup-history-wrap">
        <table className="table topup-history-table">
          <thead>
            <tr>
              <th className="col-date">{t('Date')}</th>
              {showProduct && <th className="col-product">{t('Product')}</th>}
              <th className="col-qty">{t('Qty')}</th>
              <th className="col-cost">{t('Cost')}</th>
              <th className="col-sale">{t('Sale')}</th>
              <th className="col-status">{t('Status')}</th>
              <th className="col-actions">{t('Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const unit = row.uom_code || row.product_unit || '';
              const remaining = row.qty_remaining_uom != null ? row.qty_remaining_uom : row.qty_remaining;
              return (
                <tr key={row.id} style={row.reversed ? { color: '#888' } : undefined}>
                  <td className="col-date">{formatTopUpWhen(row.created_at)}</td>
                  {showProduct && <td className="col-product">{row.product_name || '-'}</td>}
                  <td className="col-qty">{formatQty(row.qty_uom)} {unit}</td>
                  <td className="col-cost">{formatCurrency(row.cost_price || 0)}</td>
                  <td className="col-sale">{formatCurrency(row.sale_price || 0)}</td>
                  <td className="col-status">
                    {row.reversed ? (
                      <span className="topup-status-badge deleted">{t('Deleted')}</span>
                    ) : (
                      <span className={`topup-status-badge${row.legacy ? ' legacy' : ''}`}>
                        {row.source_label ? `${row.source_label} · ` : (row.legacy ? `${t('Old top-up')} · ` : '')}
                        {t('In stock')} {remaining != null ? `${formatQty(remaining)} ${unit}`.trim() : '-'}
                      </span>
                    )}
                  </td>
                  <td className="col-actions">
                    <KebabMenu
                      menuId={`topup-${row.id}`}
                      openId={openActionMenu}
                      setOpenId={setOpenActionMenu}
                      label={t('Actions')}
                      items={[
                        row.can_edit && {
                          label: t('Edit'),
                          icon: ICONS.edit,
                          onClick: () => openEditTopUp(row)
                        },
                        row.can_delete && {
                          label: topUpDeletingId === row.id ? t('Deleting...') : t('Delete'),
                          icon: ICONS.trash,
                          danger: true,
                          disabled: topUpDeletingId === row.id,
                          onClick: () => handleDeleteTopUp(row)
                        }
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const loadTopUpProduct = async (product) => {
    if (!product?.id) {
      setTopUpProduct(null);
      setTopUpUomId('');
      setTopUpQty('');
      setTopUpPrice('');
      setTopUpCost('');
      return;
    }
    try {
      const res = await axios.get(`/api/products/${product.id}`);
      fillTopUpFromProduct(res.data.product || product);
    } catch (error) {
      fillTopUpFromProduct(product);
    }
  };

  const openTopUp = (product) => {
    setEditingTopUp(null);
    setShowTopUp(true);
    if (product) {
      loadTopUpProduct(product);
    } else {
      setTopUpProduct(null);
      setTopUpUomId('');
      setTopUpQty('');
      setTopUpPrice('');
      setTopUpCost('');
    }
  };

  const handleTopUpUomChange = (uomId) => {
    setTopUpUomId(uomId);
    if (editingTopUp || !topUpProduct) return;
    const uom = topUpUom(topUpProduct, uomId);
    const price = suggestedTopUpPrice(topUpProduct, uom);
    const cost = suggestedTopUpCost(topUpProduct, uom);
    setTopUpPrice(Number.isFinite(price) ? String(price) : '');
    setTopUpCost(Number.isFinite(cost) ? String(cost) : '');
  };

  const handleTopUpSubmit = async (e) => {
    e.preventDefault();
    if (!topUpProduct?.id) {
      alert(t('Select a product to top up'));
      return;
    }
    const qty = parseFloat(topUpQty);
    const salePrice = parseFloat(topUpPrice);
    const costPrice = parseFloat(topUpCost);
    if (!(qty > 0)) {
      alert(t('Enter a quantity greater than 0'));
      return;
    }
    if (!Number.isFinite(salePrice) || salePrice < 0) {
      alert(t('Enter a sale price for the new stock'));
      return;
    }
    if (!Number.isFinite(costPrice) || costPrice < 0) {
      alert(t('Enter a cost price for the new stock'));
      return;
    }
    const uom = topUpUom(topUpProduct, topUpUomId);
    const unitLabel = uom?.code || topUpProduct.unit || 'pcs';
    const ok = window.confirm(
      editingTopUp
        ? `${t('Update this top-up to')} ${qty} ${unitLabel} at ${t('Cost')} ${formatCurrency(costPrice)} and ${t('Sale')} ${formatCurrency(salePrice)}?`
        : `${fromBaseQty(parseFloat(topUpProduct.stock_quantity) || 0, uom?.conversion_to_base || 1)} ${unitLabel} stay at the current cost and POS price. ${qty} ${unitLabel} will sell after that at cost ${formatCurrency(costPrice)} and sale ${formatCurrency(salePrice)}. Continue?`
    );
    if (!ok) return;

    setTopUpSaving(true);
    try {
      if (editingTopUp) {
        await axios.put(`/api/products/${topUpProduct.id}/top-ups/${editingTopUp.id}`, {
          quantity: qty,
          sale_price: salePrice,
          cost: costPrice,
          uom_id: uom?.id || undefined
        });
      } else {
        await axios.post(`/api/products/${topUpProduct.id}/top-up`, {
          quantity: qty,
          sale_price: salePrice,
          cost: costPrice,
          uom_id: uom?.id || undefined
        });
      }
      closeTopUp();
      fetchData();
      if (showTopUpHistory) {
        loadTopUpHistory(topUpHistoryProductId);
      }
    } catch (error) {
      alert(error.response?.data?.message || (editingTopUp ? t('Failed to update top-up') : t('Failed to top up stock')));
    } finally {
      setTopUpSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      barcode: '',
      description: '',
      category_id: '',
      subcategory_id: '',
      price: '',
      cost: '',
      stock_quantity: '',
      unit: 'pcs',
      status: 'active',
      gst_rate: '18',
      gst_inclusive: false,
      expiry_date: ''
    });
    setUoms([defaultBaseUom()]);
    setStockEntryUnit('pcs');
    setStockEntryQty('');
    setImageFile(null);
    setImagePreview('');
    setRemoveImage(false);
    setSubcategories([]);
  };

  const openModal = () => {
    resetForm();
    setEditingProduct(null);
    setShowModal(true);
  };

  const loadCostReport = async () => {
    setCostReportBusy(true);
    try {
      const res = await axios.get('/api/products/cost-report');
      setCostReport(res.data);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load cost report');
    } finally {
      setCostReportBusy(false);
    }
  };

  const downloadCostReport = () => {
    const list = (costReport && costReport.products) || [];
    const header = 'Product,SKU,Stock,Unit,Current cost,Current sale,FIFO cost,FIFO sale,Average cost,Layers';
    const lines = list.map((row) => [
      `"${String(row.name || '').replace(/"/g, '""')}"`,
      `"${String(row.sku || '').replace(/"/g, '""')}"`,
      row.stock,
      row.unit,
      row.current_cost,
      row.current_price,
      row.fifo_cost,
      row.fifo_sale,
      row.avg_cost,
      row.layer_count
    ].join(','));
    const blob = new Blob([`${header}\n${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product-cost-report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerateAllBarcodes = async () => {
    if (!window.confirm('Generate barcodes for all products that do not have one?')) {
      return;
    }
    setGeneratingBarcodes(true);
    try {
      const res = await axios.post('/api/products/generate-barcodes');
      await fetchData();
      alert(res.data.message || `Generated ${res.data.updated || 0} barcode(s)`);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to generate barcodes');
    } finally {
      setGeneratingBarcodes(false);
    }
  };

  const ensureProductBarcode = async (product) => {
    if (product.barcode && String(product.barcode).trim()) {
      return product;
    }
    const res = await axios.post(`/api/products/${product.id}/generate-barcode`);
    const updated = {
      ...product,
      ...(res.data.product || {}),
      uoms: res.data.product?.uoms?.length ? res.data.product.uoms : product.uoms
    };
    setAllProducts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated, uoms: updated.uoms || p.uoms } : p)));
    setProducts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated, uoms: updated.uoms || p.uoms } : p)));
    return updated;
  };

  const handlePrintAllBarcodes = async () => {
    if (!products.length) return;
    setGeneratingBarcodes(true);
    try {
      const ready = [];
      for (const product of products) {
        // eslint-disable-next-line no-await-in-loop
        ready.push(await ensureProductBarcode(product));
      }
      setPrintProducts(ready);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to prepare barcodes for printing');
    } finally {
      setGeneratingBarcodes(false);
    }
  };

  const handlePrintOneBarcode = async (product) => {
    setPrintingProductId(product.id);
    try {
      const ready = await ensureProductBarcode(product);
      setPrintProducts([ready]);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to generate barcode');
    } finally {
      setPrintingProductId(null);
    }
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>{t('Products')}</h1>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleGenerateAllBarcodes}
              className="btn btn-secondary"
              disabled={generatingBarcodes}
            >
              {generatingBarcodes ? 'Generating...' : 'Generate Barcodes'}
            </button>
            <button type="button" onClick={handlePrintAllBarcodes} className="btn btn-secondary" disabled={generatingBarcodes || !products.length}>
              {generatingBarcodes ? 'Preparing...' : 'Print Barcodes'}
            </button>
            {isAdmin && (
              <button type="button" onClick={loadCostReport} className="btn btn-secondary" disabled={costReportBusy}>
                {costReportBusy ? 'Loading…' : 'Cost & price report'}
              </button>
            )}
            <button type="button" onClick={() => openTopUp(null)} className="btn btn-secondary">
              {t('Top up stock')}
            </button>
            <button type="button" onClick={() => openTopUpHistory(null)} className="btn btn-secondary">
              {t('Top-up history')}
            </button>
            <button type="button" onClick={openModal} className="btn btn-primary">
            {t('Add Product')}
          </button>
          </div>
        </div>

        {isAdmin && costReport && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0 }}>Product cost and price ({costReport.count})</h3>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  View only — product cost and sale price are not changed.
                  FIFO cost / sale = oldest remaining stock. Average cost = all remaining layers.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={downloadCostReport}>Download CSV</button>
                <button type="button" className="btn btn-secondary" onClick={() => setCostReport(null)}>Hide</button>
              </div>
            </div>
            <div className="dashboard-expiry-table-wrap" style={{ marginTop: 12, maxHeight: 480, overflow: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Stock</th>
                    <th>Current cost</th>
                    <th>Current sale</th>
                    <th>FIFO cost</th>
                    <th>FIFO sale</th>
                    <th>Avg cost</th>
                  </tr>
                </thead>
                <tbody>
                  {costReport.products.map((row) => (
                    <tr key={row.id} style={
                      Math.abs(row.current_cost - row.fifo_cost) > 0.009 || Math.abs(row.current_price - row.fifo_sale) > 0.009
                        ? { background: '#fff7ed' }
                        : undefined
                    }>
                      <td>{row.name}</td>
                      <td>{row.sku || '-'}</td>
                      <td>{row.stock} {row.unit}</td>
                      <td>{formatCurrency(row.current_cost)}</td>
                      <td>{formatCurrency(row.current_price)}</td>
                      <td>{formatCurrency(row.fifo_cost)}</td>
                      <td>{formatCurrency(row.fifo_sale)}</td>
                      <td>{formatCurrency(row.avg_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Search and Filter Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="search-header">
            <h3>{t('Search Products')}</h3>
            <button onClick={clearSearch} className="btn btn-secondary btn-sm">
              {t('Clear')}
            </button>
          </div>
          <div className="search-filters">
            <div className="search-row">
              <div className="form-group">
                <label>{t('Product Name')}</label>
                <input
                  type="text"
                  placeholder="Search by name, SKU, or barcode"
                  value={searchFilters.name}
                  onChange={(e) => handleSearchChange('name', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('Category')}</label>
                <SearchableSelect
                  value={searchFilters.category_id}
                  onChange={(v) => handleSearchChange('category_id', v)}
                  placeholder={t('All Categories')}
                  options={categories.map((category) => ({
                    value: category.id,
                    label: category.name
                  }))}
                />
              </div>
            </div>
            <div className="search-results-info">
              <span>
                {products.length === 0
                  ? `${t('Showing')} 0 ${t('of')} ${allProducts.length} ${t('products')}`
                  : `${t('Showing')} ${pageStart}–${pageEnd} ${t('of')} ${products.length} ${t('products')}${
                    products.length !== allProducts.length ? ` (filtered from ${allProducts.length})` : ''
                  }`}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>Image</th>
                <th>{t('Name')}</th>
                <th>{t('SKU')}</th>
                <th>Barcode</th>
                <th>{t('Category')}</th>
                <th>{t('Subcategory')}</th>
                <th>{t('Price')}</th>
                {gstEnabled && <th>GST</th>}
                <th>{t('Stock Quantity')}</th>
                <th>Expiry</th>
                <th>{t('Status')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={gstEnabled ? 12 : 11} style={{ textAlign: 'center' }}>
                    {allProducts.length === 0 ? t('No products found') : t('No products match your search criteria')}
                  </td>
                </tr>
              ) : (
                paginatedProducts.map(product => (
                  <tr key={product.id}>
                    <td>
                      <ProductImage
                        productId={product.id}
                        hasImage={Boolean(product.has_image || product.image_filename)}
                        alt={product.name}
                        size={44}
                        className="product-list-thumb"
                        cacheKey={product.updated_at || product.image_filename || ''}
                      />
                    </td>
                    <td>{product.name}</td>
                    <td>{product.sku || '-'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{product.barcode || '-'}</td>
                    <td>{product.category_name || '-'}</td>
                    <td>{product.subcategory_name || '-'}</td>
                    <td>{formatCurrency(product.price || 0)}</td>
                    {gstEnabled && (
                      <td>
                        {Number(product.gst_rate) > 0
                          ? `${product.gst_rate}% (${Number(product.gst_inclusive) ? 'Incl.' : 'Excl.'})`
                          : '-'}
                      </td>
                    )}
                    <td>
                      {formatStockWithRetail(product)}
                      {openPriceLayers(product).length > 1 && (
                        <div style={{ fontSize: '11px', color: '#555', marginTop: '4px', lineHeight: 1.35 }}>
                          {openPriceLayers(product).map((layer, idx) => (
                            <div key={layer.id || idx}>
                              {formatLayerQty(product, layer.qty_remaining)} @ {t('Sale')} {formatCurrency(layer.sale_price)}
                              {layer.cost_price != null ? ` · ${t('Cost')} ${formatCurrency(layer.cost_price)}` : ''}
                              {idx === 0 ? ` (${t('POS first')})` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const exp = expiryStatus(product.expiry_date);
                        if (exp.status === 'none') return '-';
                        const color = exp.status === 'expired' ? '#dc2626'
                          : (exp.status === 'today' || exp.status === 'soon') ? '#d97706'
                            : '#374151';
                        return (
                          <>
                            <div>{formatDate(product.expiry_date)}</div>
                            <div style={{ fontSize: 11, color, fontWeight: exp.status === 'ok' ? 400 : 600 }}>
                              {expiryLabel(exp.days)}
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      <span className={`status-badge status-${product.status}`}>
                        {product.status}
                      </span>
                    </td>
                    <td>
                      <KebabMenu
                        menuId={`product-${product.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        label={t('Actions')}
                        items={[
                          {
                            label: printingProductId === product.id ? t('Preparing...') : t('Print barcode'),
                            icon: ICONS.barcode,
                            disabled: printingProductId === product.id,
                            onClick: () => handlePrintOneBarcode(product)
                          },
                          {
                            label: t('Top up'),
                            icon: ICONS.topUp,
                            onClick: () => openTopUp(product)
                          },
                          {
                            label: t('Top-up history'),
                            icon: ICONS.history,
                            onClick: () => openTopUpHistory(product)
                          },
                          {
                            label: t('Edit'),
                            icon: ICONS.edit,
                            onClick: () => handleEdit(product)
                          },
                          {
                            label: t('Delete'),
                            icon: ICONS.trash,
                            danger: true,
                            onClick: () => handleDelete(product.id)
                          }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {products.length > 0 && (
            <div className="products-pagination">
                      <button
                type="button"
                        className="btn btn-secondary btn-sm"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage <= 1}
                      >
                Previous
                      </button>
              <div className="products-pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    return Math.abs(page - safePage) <= 1;
                  })
                  .reduce((acc, page, idx, arr) => {
                    if (idx > 0 && page - arr[idx - 1] > 1) acc.push('…');
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item, idx) => (
                    item === '…' ? (
                      <span key={`ellipsis-${idx}`} className="products-pagination-ellipsis">…</span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`btn btn-sm ${item === safePage ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => goToPage(item)}
                      >
                        {item}
                      </button>
                    )
                  ))}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {printProducts && (
          <ProductBarcodePrint
            products={printProducts}
            onClose={() => setPrintProducts(null)}
          />
        )}

        {showTopUp && (
          <div className="modal">
            <div className="modal-content topup-history-modal">
              <div className="modal-header">
                <h2>{editingTopUp ? t('Edit top-up') : t('Top up existing product')}</h2>
                <button className="close-btn" onClick={closeTopUp} type="button">×</button>
              </div>
              <form onSubmit={handleTopUpSubmit}>
                <p style={{ marginTop: 0, color: '#444', lineHeight: 1.45 }}>
                  {editingTopUp
                    ? t('Change quantity, cost, or sale price for this top-up. Quantity cannot go below what was already sold.')
                    : t('Add more stock without changing the price of units already on the shelf. POS sells old quantity at the old price first, then the topped-up quantity at the new price.')}
                </p>
                <div className="form-group">
                  <label>{t('Product')} *</label>
                  {editingTopUp ? (
                    <div>{topUpProduct?.name || editingTopUp.product_name || '-'}</div>
                  ) : (
                    <SearchableProductSelect
                      products={allProducts}
                      value={topUpProduct?.id || ''}
                      onChange={(id) => {
                        const picked = allProducts.find((p) => String(p.id) === String(id));
                        loadTopUpProduct(picked || null);
                      }}
                      required
                    />
                  )}
                </div>
                {topUpProduct && (
                  <>
                    <div className="form-group">
                      <label>{t('Currently in stock')}</label>
                      <div>
                        {(() => {
                          const uom = topUpUom(topUpProduct, topUpUomId);
                          const oldInUom = fromBaseQty(
                            parseFloat(topUpProduct.stock_quantity) || 0,
                            uom?.conversion_to_base || 1
                          );
                          const posPrice = suggestedTopUpPrice(topUpProduct, uom);
                          const posCost = suggestedTopUpCost(topUpProduct, uom);
                          return `${oldInUom} ${uom?.code || topUpProduct.unit || ''} — ${t('Sale')} ${formatCurrency(posPrice)}, ${t('Cost')} ${formatCurrency(posCost)} (${t('stays until sold')})`;
                        })()}
                      </div>
                      {openPriceLayers(topUpProduct).length > 1 && (
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '6px' }}>
                          {openPriceLayers(topUpProduct).map((layer, idx) => (
                            <div key={layer.id || idx}>
                              {formatLayerQty(topUpProduct, layer.qty_remaining)} @ {t('Sale')} {formatCurrency(layer.sale_price)}
                              {layer.cost_price != null ? ` · ${t('Cost')} ${formatCurrency(layer.cost_price)}` : ''}
                              {idx === 0 ? ` — ${t('sold first')}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>{t('Quantity to add')} *</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={topUpQty}
                        onChange={(e) => setTopUpQty(e.target.value)}
                        required
                      />
                    </div>
                    {(topUpProduct.uoms || []).length > 0 && (
                      <div className="form-group">
                        <label>{t('Unit')}</label>
                        <select
                          value={topUpUomId}
                          onChange={(e) => handleTopUpUomChange(e.target.value)}
                        >
                          {(topUpProduct.uoms || []).map((uom) => (
                            <option key={uom.id} value={uom.id}>
                              {uom.code || uom.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-group">
                      <label>{t('Sale price for this top-up')} *</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={topUpPrice}
                        onChange={(e) => setTopUpPrice(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('Cost price for this top-up')} *</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={topUpCost}
                        onChange={(e) => setTopUpCost(e.target.value)}
                        required
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                        {t('Change sale and cost if the new pieces came in at different rates. Existing stock is not repriced.')}
                      </small>
                    </div>
                  </>
                )}
                {topUpProduct && (
                  <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                    <h3 style={{ marginBottom: '10px' }}>{t('Top-up history')}</h3>
                    {renderTopUpHistoryTable(
                      topUpHistory.filter((row) => String(row.product_id) === String(topUpProduct.id)),
                      false
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" className="btn btn-secondary" onClick={closeTopUp}>
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={topUpSaving || !topUpProduct}>
                    {topUpSaving ? t('Saving...') : (editingTopUp ? t('Save') : t('Top up'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showTopUpHistory && (
          <div className="modal">
            <div className="modal-content topup-history-modal">
              <div className="modal-header">
                <h2>{t('Top-up history')}</h2>
                <button className="close-btn" onClick={() => setShowTopUpHistory(false)} type="button">×</button>
              </div>
              <p style={{ marginTop: 0, color: '#444' }}>
                {t('Each line is a stock lot: top-up, opening stock, or purchase. Qty is what was added; In stock is what is still left in that lot, in the same unit as the product column.')}
              </p>
              {renderTopUpHistoryTable(topUpHistory, true)}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTopUpHistory(false)}>
                  {t('Close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingProduct ? t('Edit Product') : t('Add Product')}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('Product Name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('SKU')}</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Barcode</label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => {
                      const barcode = e.target.value;
                      setFormData({ ...formData, barcode });
                      setUoms((prev) => prev.map((row) => (
                        row.is_base ? { ...row, barcode } : row
                      )));
                    }}
                    placeholder={editingProduct ? 'Leave blank to clear' : 'Auto-generated if empty'}
                  />
                  {!editingProduct && (
                    <small style={{ color: '#6b7280' }}>Leave empty to auto-generate a unique barcode</small>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('Description')}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Product Image</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setImageFile(file);
                      setRemoveImage(false);
                      if (imagePreview) URL.revokeObjectURL(imagePreview);
                      setImagePreview(file ? URL.createObjectURL(file) : '');
                    }}
                  />
                  <small style={{ color: '#6b7280', display: 'block', marginTop: 4 }}>
                    JPG, PNG, WEBP or GIF · max 8 MB
                  </small>
                  <div className="product-image-form-preview">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" />
                    ) : (
                      !removeImage && editingProduct && (editingProduct.has_image || editingProduct.image_filename) ? (
                        <ProductImage
                          productId={editingProduct.id}
                          hasImage
                          alt={editingProduct.name}
                          size={96}
                        />
                      ) : (
                        <div className="product-image-placeholder" style={{ width: 96, height: 96 }} />
                      )
                    )}
                    {(imagePreview || (!removeImage && editingProduct && (editingProduct.has_image || editingProduct.image_filename))) && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setImageFile(null);
                          if (imagePreview) URL.revokeObjectURL(imagePreview);
                          setImagePreview('');
                          setRemoveImage(true);
                        }}
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>{t('Category')}</label>
                    <SearchableSelect
                      value={formData.category_id}
                      onChange={(v) => setFormData({ ...formData, category_id: v, subcategory_id: '' })}
                      placeholder={t('Select Category')}
                      options={categories.map((category) => ({
                        value: category.id,
                        label: category.name
                      }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('Subcategory')}</label>
                    <SearchableSelect
                      value={formData.subcategory_id}
                      onChange={(v) => setFormData({ ...formData, subcategory_id: v })}
                      placeholder={t('Select Subcategory')}
                      disabled={!formData.category_id}
                      options={subcategories.map((subcategory) => ({
                        value: subcategory.id,
                        label: subcategory.name
                      }))}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Price per {baseUomCode || 'unit'} *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                    />
                    <small style={{ color: '#6b7280' }}>
                      Saving a new price updates remaining stock on POS. Old stock cost is not changed. Use Top up to add new stock at a different rate.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>{t('Cost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Expiry date (optional)</label>
                  <DateInput
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                  <small style={{ color: '#6b7280' }}>Leave empty if this product does not expire.</small>
                </div>
                {gstEnabled && (
                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '14px',
                    marginBottom: '15px',
                    background: '#fafafa'
                  }}>
                    <strong style={{ display: 'block', marginBottom: '10px' }}>GST Settings</strong>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                        <label>GST %</label>
                        <select
                          value={gstRateSelectValue(formData.gst_rate, '0')}
                          onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                        >
                          {GST_RATES.map((rate) => (
                            <option key={rate} value={String(rate)}>{rate}%</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Price includes GST?</label>
                        <select
                          value={formData.gst_inclusive ? '1' : '0'}
                          onChange={(e) => setFormData({ ...formData, gst_inclusive: e.target.value === '1' })}
                        >
                          <option value="0">Exclude GST (add GST on invoice)</option>
                          <option value="1">Include GST (price already has GST)</option>
                        </select>
                      </div>
                    </div>
                    {formData.price !== '' && Number(formData.gst_rate) > 0 && (() => {
                      const priceInfo = priceBreakdown(formData.price, formData.gst_rate, formData.gst_inclusive);
                      const costInfo = formData.cost !== ''
                        ? priceBreakdown(formData.cost, formData.gst_rate, formData.gst_inclusive)
                        : null;
                      return (
                        <div style={{ fontSize: '13px', color: '#444', lineHeight: 1.5 }}>
                          <div>
                            <strong>Price:</strong>{' '}
                            Base {formatCurrency(priceInfo.base)} + CGST {formatCurrency(priceInfo.cgst)} + SGST {formatCurrency(priceInfo.sgst)} = {formatCurrency(priceInfo.total)}
                          </div>
                          {Number(formData.gst_rate) > 0 && (
                            <div style={{ color: '#666' }}>
                              CGST {(Number(formData.gst_rate) / 2)}% | SGST {(Number(formData.gst_rate) / 2)}%
                            </div>
                          )}
                          {costInfo && (
                            <div>
                              <strong>Cost:</strong>{' '}
                              Base {formatCurrency(costInfo.base)} + CGST {formatCurrency(costInfo.cgst)} + SGST {formatCurrency(costInfo.sgst)} = {formatCurrency(costInfo.total)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: 14,
                  marginBottom: 16,
                  background: '#f8fafc'
                }}>
                  <strong style={{ display: 'block', marginBottom: 4 }}>How do you sell this?</strong>
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
                    Stock is counted in one unit. Add a pack only if you also sell a bigger or smaller size.
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label>Stock is counted in</label>
                      <select
                        value={COMMON_UNITS.some((u) => normalizeUnitCode(u.code) === normalizeUnitCode(baseUomCode)) ? baseUomCode : '__other__'}
                        onChange={(e) => {
                          if (e.target.value === '__other__') setBaseUnitCode('');
                          else setBaseUnitCode(e.target.value);
                        }}
                      >
                        {COMMON_UNITS.map((u) => (
                          <option key={u.code} value={u.code}>{u.name}</option>
                        ))}
                        <option value="__other__">Other…</option>
                      </select>
                      {!COMMON_UNITS.some((u) => normalizeUnitCode(u.code) === normalizeUnitCode(baseUomCode)) && (
                    <input
                          type="text"
                          style={{ marginTop: 6 }}
                          value={baseUomCode}
                          onChange={(e) => setBaseUnitCode(e.target.value)}
                          placeholder="Type a unit, e.g. Dozen"
                        />
                      )}
                  </div>
                  <div className="form-group">
                      <label>{t('Stock Quantity')}</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="number"
                          step="any"
                          value={stockEntryQty}
                          onChange={(e) => {
                            const v = e.target.value;
                            setStockEntryQty(v);
                            applyStockEntryToBase(v, stockEntryUnit, baseUomCode);
                          }}
                          placeholder="0"
                        />
                    <select
                          value={stockEntryUnit}
                          onChange={(e) => {
                            const nextUnit = e.target.value;
                            setStockEntryUnit(nextUnit);
                            applyStockEntryToBase(stockEntryQty, nextUnit, baseUomCode);
                          }}
                          style={{ maxWidth: 120 }}
                        >
                          <option value={baseUomCode}>{baseUomCode}</option>
                          {uoms.filter((u) => !u.is_base && u.code && normalizeUnitCode(u.code) !== normalizeUnitCode(baseUomCode)).map((u) => (
                            <option key={u.code} value={u.code}>{u.code}</option>
                          ))}
                    </select>
                      </div>
                      {stockEntryUnit && normalizeUnitCode(stockEntryUnit) !== normalizeUnitCode(baseUomCode) && stockEntryQty !== '' && (
                        <small style={{ color: '#64748b' }}>
                          = {formData.stock_quantity || 0} {baseUomCode}
                        </small>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <div style={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '10px 12px',
                      marginBottom: 12
                    }}>
                      <strong style={{ display: 'block', marginBottom: 6 }}>Sell one piece from this pack</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        <span>1 {baseUomCode || 'unit'} contains</span>
                        <input
                          type="number"
                          min="2"
                          step="1"
                          value={innerCount}
                          onChange={(e) => setInnerCount(e.target.value)}
                          style={{ width: 80 }}
                          aria-label="How many pieces inside"
                        />
                        <input
                          type="text"
                          list="common-uom-codes"
                          value={innerName}
                          onChange={(e) => setInnerName(e.target.value)}
                          placeholder="Packet"
                          style={{ width: 110 }}
                          aria-label="Small piece name"
                        />
                        <button type="button" className="btn btn-secondary btn-sm" onClick={addSmallerPiece}>
                          Add
                        </button>
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
                        Set a retail price on the small unit, and a separate price on the Box, Bdl, or Bag.
                      </p>
                    </div>
                    <label style={{ display: 'block', marginBottom: 8 }}>Also sell as a bigger pack</label>
                    {uoms.filter((u) => !u.is_base).length === 0 && (
                      <p style={{ margin: '0 0 8px', fontSize: 13, color: '#94a3b8' }}>
                        None yet. Example: 1 Box = 20 {baseUomCode || 'pcs'}
                      </p>
                    )}
                    <datalist id="common-uom-codes">
                      {COMMON_UNITS.map((u) => (
                        <option key={u.code} value={u.code}>{u.name}</option>
                      ))}
                    </datalist>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {uoms.map((u, index) => {
                        if (u.is_base) return null;
                        const factor = parseFloat(u.conversion_to_base);
                        const isSmaller = factor > 0 && factor < 1;
                        const pieces = isSmaller ? piecesFromInnerFactor(factor) : '';
                        const sentence = isSmaller
                          ? `1 ${baseUomCode} contains ${pieces} ${u.code || 'pieces'}. Selling 1 uses 1/${pieces} of the stock.`
                          : (factor
                            ? `1 ${u.code || 'pack'} = ${u.conversion_to_base} ${baseUomCode}`
                            : `How many ${baseUomCode} are in 1 pack?`);
                        return (
                          <div key={index} style={{
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            padding: '10px 12px'
                          }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                              {isSmaller ? (
                                <>
                                  <span>1 {baseUomCode} contains</span>
                                  <input
                                    type="number"
                                    step="any"
                                    min="1"
                                    value={pieces}
                                    onChange={(e) => {
                                      const n = parseFloat(e.target.value);
                                      const nextFactor = n > 0 ? innerFactorFromPieces(n) : '';
                                      setUoms((prev) => prev.map((row, i) => (i === index ? { ...row, conversion_to_base: nextFactor } : row)));
                                    }}
                                    style={{ width: 80 }}
                                    aria-label={`How many ${u.code || 'pieces'} in one ${baseUomCode}`}
                                  />
                                  <input
                                    type="text"
                                    list="common-uom-codes"
                                    value={u.code}
                                    onChange={(e) => syncUomCode(index, e.target.value)}
                                    placeholder="Packet"
                                    style={{ width: 110 }}
                                    aria-label="Piece name"
                                  />
                                </>
                              ) : (
                                <>
                                  <span>1</span>
                                  <input
                                    type="text"
                                    list="common-uom-codes"
                                    value={u.code}
                                    onChange={(e) => syncUomCode(index, e.target.value)}
                                    placeholder="Box"
                                    style={{ width: 110 }}
                                    aria-label="Pack name"
                                  />
                                  <span>=</span>
                                  <input
                                    type="number"
                                    step="any"
                                    min="0"
                                    value={u.conversion_to_base}
                                    onChange={(e) => setUoms((prev) => prev.map((row, i) => (i === index ? { ...row, conversion_to_base: e.target.value } : row)))}
                                    placeholder="20"
                                    style={{ width: 90 }}
                                    aria-label={`How many ${baseUomCode} in one pack`}
                                  />
                                  <span>{baseUomCode}</span>
                                </>
                              )}
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => setUoms((prev) => prev.filter((_, i) => i !== index))}
                                style={{ marginLeft: 'auto' }}
                              >
                                Remove
                              </button>
                            </div>
                            <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>{sentence}</div>
                            <div className="form-group" style={{ marginTop: 8, marginBottom: 0 }}>
                              <label>{isSmaller ? `Retail price per ${u.code || 'piece'}` : `Price per ${u.code || 'pack'}`}</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder={isSmaller ? 'Retail price' : 'Box, Bdl, or Bag price'}
                                value={u.sale_price}
                                onChange={(e) => setUoms((prev) => prev.map((row, i) => (i === index ? { ...row, sale_price: e.target.value } : row)))}
                              />
                            </div>
                            {showUomExtra && (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                                <input
                                  type="number"
                                  step="any"
                                  placeholder="Purchase price"
                                  value={u.purchase_price}
                                  onChange={(e) => setUoms((prev) => prev.map((row, i) => (i === index ? { ...row, purchase_price: e.target.value } : row)))}
                                />
                                <input
                                  type="text"
                                  placeholder="Barcode"
                                  value={u.barcode}
                                  onChange={(e) => {
                                    const barcode = e.target.value;
                                    setUoms((prev) => prev.map((row, i) => (i === index ? { ...row, barcode } : row)));
                                    if (u.is_base) {
                                      setFormData((fd) => ({ ...fd, barcode }));
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setUoms((prev) => [...prev, blankUom(false)])}
                      >
                        + Add pack
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={applyPackPreset}>
                        Example: water (1 Box = 20 Bottles)
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={applyWeightPreset}>
                        Example: sugar (kg, g, 500g)
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowUomExtra((v) => !v)}
                      >
                        {showUomExtra ? 'Hide prices & barcodes' : 'Prices & barcodes'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('Status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="inactive">{t('Inactive')}</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingProduct ? t('Update') : t('Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
